/**
 * useNotifications Hook
 * 
 * Manages toast/notification state for the application.
 */

import { useState, useCallback } from 'react';
import { Notification, NotificationType } from '../types';

export interface AddNotificationOptions {
  /** Explicit id (e.g. a server-side notification id) instead of a random one. */
  id?: string;
  /** Defaults to true. Set false for a notification with no close button that never auto-dismisses. */
  dismissible?: boolean;
  duration?: number;
  /** Optional action button (e.g. "View findings") — clicking runs onClick and dismisses. */
  action?: { label: string; onClick: () => void };
}

export interface UseNotificationsReturn {
  notifications: Notification[];
  addNotification: (
    type: NotificationType,
    message: string,
    description?: string,
    options?: AddNotificationOptions,
  ) => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
}

export function useNotifications(): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = useCallback((
    type: NotificationType,
    message: string,
    description?: string,
    options?: AddNotificationOptions,
  ) => {
    const id = options?.id ?? Math.random().toString(36).substring(2, 9);
    setNotifications(prev => {
      if (prev.some(n => n.id === id)) return prev; // already showing (e.g. re-polled)
      return [...prev, {
        id,
        type,
        message,
        description,
        dismissible: options?.dismissible,
        duration: options?.duration,
        action: options?.action,
      }];
    });
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  return {
    notifications,
    addNotification,
    removeNotification,
    clearAll
  };
}

